import type { ZohoOauthCredentials } from '@kunzite/shared-types';

import { ApiError, AuthExpiredError, detectAuthError } from './calls-api';

export interface RefreshOn401Deps {
  baseUrl: string;
  fetch: typeof globalThis.fetch;
  onJwtUpdate?: (jwt: string, fresh: { expiresAt: string; refreshExpiresAt: string; zohoOauth: ZohoOauthCredentials }) => void;
}

interface RefreshResponseBody {
  jwt: string;
  expiresAt: string;
  refreshExpiresAt: string;
  zohoOauth: ZohoOauthCredentials;
}

/**
 * Shared 401-retry helper. Runs `doRequest(jwt)`. If the response is a typed
 * auth error (oauth_revoked / cipher_corrupt) it throws the matching Error
 * subclass via `detectAuthError`. If it's an untyped 401, posts to
 * `/v1/auth/refresh` (cookie-based refresh token), invokes `onJwtUpdate`,
 * and retries `doRequest(newJwt)` exactly once. A second 401 throws
 * `AuthExpiredError` (no infinite loop). On 2xx the response is handed to
 * `parseSuccess` which produces the typed return value.
 */
export async function refreshOn401<T>(
  deps: RefreshOn401Deps,
  doRequest: (jwt: string) => Promise<Response>,
  parseSuccess: (res: Response) => Promise<T>,
  initialJwt: string,
): Promise<T> {
  // Hoist deps.fetch into a local so the call is unbound. Browsers' window.fetch
  // throws "Illegal invocation" when called as deps.fetch(...) because the
  // property access binds `this = deps`. F4.2-acceptance HF3 regression fix.
  const fetcher = deps.fetch;
  const res = await doRequest(initialJwt);
  if (res.status === 401 || res.status === 500) {
    const typed = await detectAuthError(res);
    if (typed) throw typed;
  }
  if (res.status !== 401) {
    return parseSuccess(res);
  }
  const refreshRes = await fetcher(`${deps.baseUrl}/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });
  if (refreshRes.status === 401) {
    throw new AuthExpiredError();
  }
  if (!refreshRes.ok) {
    throw new ApiError(refreshRes.status, await refreshRes.text());
  }
  const fresh = (await refreshRes.json()) as RefreshResponseBody;
  if (deps.onJwtUpdate) {
    deps.onJwtUpdate(fresh.jwt, {
      expiresAt: fresh.expiresAt,
      refreshExpiresAt: fresh.refreshExpiresAt,
      zohoOauth: fresh.zohoOauth,
    });
  }
  const res2 = await doRequest(fresh.jwt);
  if (res2.status === 401 || res2.status === 500) {
    const typed2 = await detectAuthError(res2);
    if (typed2) throw typed2;
  }
  if (res2.status === 401) {
    throw new AuthExpiredError();
  }
  return parseSuccess(res2);
}
